import React, { useEffect, useRef, useState } from 'react';

import currencyFormatter from 'currency-formatter';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';

import Calendar from '~/components/Common/Calendar';
import BlackoutDateForm from '~/components/Common/Forms/BlackoutDateForm';
import InventoryForm from '~/components/Common/Forms/InventoryForm';
import SurchargeDateForm from '~/components/Common/Forms/SurchargeDateForm';
import convertDateListToRanges from '~/components/Common/Forms/helpers/convertDateListToRanges';
import Selection from '~/components/Common/Selection';
import {
  dateHasSurcharge,
  dateIsBlackout,
  getAvailabilityForDate,
} from '~/components/Vendors/Properties/helpers/availability';
import getCalendarGrid from '~/components/Vendors/Properties/helpers/getCalendarGrid';

import { useGetFxRates } from '~/hooks/apis/fx/useGetFxRates';
import { useGetRoomRateAvailability } from '~/hooks/apis/reservation/useGetRoomRateAvailability';
import { useGetRoomRateBlackoutDates } from '~/hooks/apis/reservation/useGetRoomRateBlackoutDates';
import { useGetRoomRateSurchargeDates } from '~/hooks/apis/reservation/useGetRoomRateSurchargeDates';
import { useGetSurchargeDateBlockSchema } from '~/hooks/apis/reservation/useGetSurchargeDateBlockSchema';
import { useGetVendor } from '~/hooks/apis/vendors/useGetVendor';

import { dateNow } from '~/services/TimeService';

import presentFXRates from '~/utils/presentFXRates';

import { dayCompareFunc } from '../../utils';

import InventoryCalendarDay from './InventoryCalendarDay';

const MONTHS_VISIBLE = 2;
const MONTHS_TOTAL = 24;

interface SelectedDate {
  date: Date;
  count?: number;
  total?: number;
}

interface Props {
  propertyId: string;
  vendorId: string;
  roomType: App.RoomType;
  roomRate: App.RoomRate;
  propertyRoomTypes: Array<App.RoomType>;
  channelManaged: boolean;
}

export default function InventoryTab({
  propertyId,
  vendorId,
  roomType,
  roomRate,
  propertyRoomTypes,
  channelManaged,
}: Props) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectMode, setSelectMode] = useState<'free' | 'range'>('free');
  const [selectedDates, setSelectedDates] = useState<Array<SelectedDate>>([]);
  const [settingInventory, setSettingInventory] = useState(false);
  const [addingBlackout, setAddingBlackout] = useState(false);
  const [addingSurcharge, setAddingSurcharge] = useState(false);
  const [selectedRange, setSelectedRange] = useState<{ start?: Date; end?: Date }>({});
  const selectionRef = useRef<Selection>(null);
  const calendarRef = useRef<Calendar>(null);
  const selectedDayIndicesRef = useRef<Array<number>>([]);

  // Mirrors RoomTypeDatesEdit's componentDidMount: Selection needs the Calendar's day refs to compute
  // drag/range intersections against. Re-runs whenever the calendar page changes and remounts (key={monthOffset}).
  useEffect(() => {
    selectionRef.current?.setSelectableRefs(calendarRef.current?.days ?? []);
  });

  const {
    data: availability = [],
    isLoading: isLoadingAvailability,
    error: availabilityError,
    refetch: refetchAvailability,
  } = useGetRoomRateAvailability(propertyId, roomType.id, roomRate.id);
  const {
    data: blackoutDates = [],
    error: blackoutError,
    refetch: refetchBlackoutDates,
  } = useGetRoomRateBlackoutDates(propertyId, roomType.id, roomRate.id);
  const {
    data: surchargeDates = [],
    error: surchargeError,
    refetch: refetchSurchargeDates,
  } = useGetRoomRateSurchargeDates(propertyId, roomType.id, roomRate.id);
  // Blackout/surcharge errors still leave the calendar rendering (with an empty [] fallback), so a fetch failure
  // there would otherwise silently show a day as available when it may not be — surface it alongside availability.
  const error = availabilityError || blackoutError || surchargeError;
  const { data: surchargeSchema } = useGetSurchargeDateBlockSchema(propertyId, roomType.id, roomRate.id);
  const { data: fxRates } = useGetFxRates();
  const { data: vendor } = useGetVendor(vendorId);
  const vendorCurrencyCode = vendor?.currencyisocode;

  const grid = getCalendarGrid(dateNow(), MONTHS_VISIBLE, monthOffset).map((month) => ({
    date: month.date,
    weeks: month.weeks.map((week) =>
      week.map((day) => {
        const surcharge = dateHasSurcharge(day, surchargeDates);
        const surchargeAmount = vendorCurrencyCode ? surcharge?.prices?.[vendorCurrencyCode] : undefined;
        return {
          day,
          availability: getAvailabilityForDate(day, availability),
          blackout: dateIsBlackout(day, blackoutDates),
          surcharge,
          surchargeLabel:
            typeof surchargeAmount === 'number'
              ? `${currencyFormatter.format(surchargeAmount, { code: vendorCurrencyCode, precision: 0 })} ${vendorCurrencyCode}`
              : undefined,
        };
      }),
    ),
  }));

  function onSelectionChange(selections: Array<string>) {
    if (channelManaged) {
      return;
    }
    const calendarDays = calendarRef.current?.days ?? [];
    const newSelectedDayIndices = selections.map((index) => Number(index));

    selectedDayIndicesRef.current.forEach((dayIndex) => {
      if (!newSelectedDayIndices.includes(dayIndex)) {
        calendarDays[dayIndex]?.setSelected(false);
      }
    });
    newSelectedDayIndices.forEach((dayIndex) => {
      calendarDays[dayIndex]?.setSelected(true);
    });
    selectedDayIndicesRef.current = newSelectedDayIndices;

    const days = grid.flatMap((month) => month.weeks.flat()).filter((day) => day?.day);
    const nextSelectedDates = newSelectedDayIndices
      .map((index) => days[index])
      .filter(Boolean)
      .map((day) => ({ date: day.day, count: day.availability?.count, total: day.availability?.total }));
    setSelectedDates(nextSelectedDates);
  }

  function showInventoryForm() {
    if (selectedDates.length > 0) {
      setSettingInventory(true);
    }
  }

  function showBlackoutForm() {
    const ranges = convertDateListToRanges(selectedDates);
    if (selectMode === 'range' && ranges[0]) {
      setSelectedRange(ranges[0]);
      setAddingBlackout(true);
    }
  }

  function showSurchargeForm() {
    const ranges = convertDateListToRanges(selectedDates);
    if (selectMode === 'range' && ranges[0]) {
      setSelectedRange(ranges[0]);
      setAddingSurcharge(true);
    }
  }

  const onlyEmptyCellSelected = selectedDates.every((date) => !date.total);

  function changeMonthOffset(delta: number) {
    setMonthOffset((offset) => offset + delta);
    setSelectedDates([]);
    selectedDayIndicesRef.current = [];
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each day shows <strong>bought / loaded</strong> — for example <strong>0 / 5</strong> means 0 bought of 5 loaded.
        Select one or more dates, then use Set inventory, Set blackout, or Set surcharge.
        {channelManaged && (
          <Box component="span" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
            *Availability controlled by channel manager, e.g. Siteminder
          </Box>
        )}
      </Typography>

      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        {!channelManaged && (
          <Stack direction="row" flexWrap="wrap" spacing={1} alignItems="center">
            <Typography variant="subtitle2" mr={1}>
              Select mode:
            </Typography>
            <FormControl>
              <RadioGroup
                row
                value={selectMode}
                onChange={(event) => setSelectMode(event.target.value as 'free' | 'range')}
              >
                <FormControlLabel value="free" control={<Radio size="small" />} label="Free select" />
                <FormControlLabel value="range" control={<Radio size="small" />} label="Range" />
              </RadioGroup>
            </FormControl>
            <ButtonGroup variant="contained" size="small">
              <Button onClick={showInventoryForm} sx={{ opacity: selectedDates.length === 0 ? 0.6 : 1 }}>
                Set inventory
              </Button>
              <Tooltip
                title="Blackouts can only be applied in range mode"
                disableHoverListener={selectMode === 'range'}
              >
                <Button onClick={showBlackoutForm} sx={{ opacity: selectMode !== 'range' ? 0.6 : 1 }}>
                  Set blackout
                </Button>
              </Tooltip>
              <Tooltip
                title="Surcharges can only be applied in range mode"
                disableHoverListener={selectMode === 'range'}
              >
                <Button onClick={showSurchargeForm} sx={{ opacity: selectMode !== 'range' ? 0.6 : 1 }}>
                  Set surcharge
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Stack>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: '#b6e7ff', border: '1px solid #7ca5b7' }} />
            <Typography variant="caption" color="text.secondary">
              Selected
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: (theme) => alpha(theme.palette.warning.light, 0.3),
                borderLeft: '3px solid',
                borderLeftColor: 'warning.light',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Surcharge
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: 'grey.100',
                backgroundImage: (theme) =>
                  `repeating-linear-gradient(-45deg, ${theme.palette.grey[300]}, ${theme.palette.grey[300]} 2px, transparent 2px, transparent 5px)`,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Blackout
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={() => changeMonthOffset(-MONTHS_VISIBLE)} disabled={monthOffset === 0}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              disabled={monthOffset + MONTHS_VISIBLE >= MONTHS_TOTAL}
              onClick={() => changeMonthOffset(MONTHS_VISIBLE)}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error">Unable to load inventory.</Alert>
      ) : isLoadingAvailability ? (
        <CircularProgress size={24} />
      ) : (
        <Box
          sx={{
            '& .inline-group': { display: 'flex' },
            '& .month': { flex: '1 1 0', width: 'auto' },
            '& .week': { display: 'flex' },
            '& .day-name': { flex: '1 1 0', width: 'auto' },
            '& .day-container': { flex: '1 1 0', width: 'auto', height: 'auto' },
            '& .availability-day': {
              flex: '1 1 0',
              width: 'auto',
              height: 80,
              padding: '0.4rem 3px',
              marginRight: 0,
              boxSizing: 'border-box',
              justifyContent: 'center',
            },
          }}
        >
          <Selection
            key={monthOffset}
            ref={selectionRef}
            onSelectionChange={onSelectionChange}
            selectMode={selectMode}
            rangeCompareFunc={dayCompareFunc}
          >
            <Calendar ref={calendarRef} grid={grid} calendarDayComponent={InventoryCalendarDay} />
          </Selection>
        </Box>
      )}

      <Dialog
        open={settingInventory}
        onClose={() => setSettingInventory(false)}
        maxWidth={onlyEmptyCellSelected ? 'sm' : 'md'}
        fullWidth
      >
        <DialogTitle>Apply Inventory</DialogTitle>
        <DialogContent>
          <InventoryForm
            dates={selectedDates}
            onlyEmptyCellSelected={onlyEmptyCellSelected}
            propertyId={propertyId}
            roomType={roomType}
            propertyRoomTypes={propertyRoomTypes}
            onCancel={() => setSettingInventory(false)}
            onInventoryUpdated={refetchAvailability}
            onSuccess={() => setSettingInventory(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={addingBlackout} onClose={() => setAddingBlackout(false)}>
        <DialogTitle>Apply Blackout Dates</DialogTitle>
        <DialogContent>
          <BlackoutDateForm
            propertyId={propertyId}
            roomType={roomType}
            roomRate={roomRate}
            propertyRoomTypes={propertyRoomTypes}
            onCancel={() => setAddingBlackout(false)}
            onBlackoutDateBlocksAdded={refetchBlackoutDates}
            onSuccess={() => setAddingBlackout(false)}
            startDate={selectedRange.start}
            endDate={selectedRange.end}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={addingSurcharge} onClose={() => setAddingSurcharge(false)}>
        <DialogTitle>Apply Surcharge Dates</DialogTitle>
        <DialogContent>
          {surchargeSchema && (
            <SurchargeDateForm
              propertyId={propertyId}
              roomType={roomType}
              roomRate={roomRate}
              propertyRoomTypes={propertyRoomTypes}
              onCancel={() => setAddingSurcharge(false)}
              vendorCurrencyCode={vendor?.currencyisocode}
              onSurchargeDateBlocksAdded={refetchSurchargeDates}
              onSuccess={() => setAddingSurcharge(false)}
              startDate={selectedRange.start}
              endDate={selectedRange.end}
              surchargeSchema={surchargeSchema}
              foreignExchangeRates={fxRates ? presentFXRates(fxRates) : {}}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
